import { Scene } from '../Scene';
import { SceneManifest } from '../SceneManifest';
import { RenderObject } from '../../object/RenderObject';
import { applyModelLodKitToGroup, buildModelLodKit } from './ModelLodKit';
import type { InstanceGroup } from '../../renderer/InstanceGroup';

/**
 * Per-model LOD discovery driven by published GLB extras:
 *   - Root extras `_MODEL_LOD` describes the full ordered LOD config (decimated
 *     + billboard tiers, plus optional crossfade band).
 *   - Decimated tiers are emitted as standalone GLB meshes whose mesh-level
 *     extras carry `_LOD_TIER` (1-based ascending detail loss) and
 *     `_LOD_SOURCE_MESH` (back-pointer to the source mesh index). Per-primitive
 *     `_LOD` extras carry the matching `maxDistance` so we don't re-derive it
 *     from the entries list when registering on InstanceGroups.
 *   - Billboard tier (if any) is generated at runtime via CrossBillboardMesh.
 *
 * Builds a per-model "LOD kit" (cached on `scene.modelLodKits`) for ALL models
 * that carry `_MODEL_LOD` extras — regardless of whether any object of that
 * model exists at scene-load time. Tile-streamed objects' InstanceGroups come
 * and go with tile load/unload; the streamer re-applies the kit on every
 * fresh group, so the model-keyed cache is what makes that cheap.
 *
 * After kit building, walks `globalStaticObjects` to apply each kit to the
 * matching groups (full-scene-resident objects' groups never disappear).
 */
export function registerLods(
  scene: Scene,
  manifest: SceneManifest,
  namedObjects: Map<string, RenderObject[]>,
): void {
  // 1. Build kits for every model in the registry that carries _MODEL_LOD.
  for (const [modelName, model] of scene.modelRegistry.entries()) {
    if (scene.modelLodKits.has(modelName)) continue;
    const kit = buildModelLodKit(scene, modelName, model);
    if (kit) scene.modelLodKits.set(modelName, kit);
  }

  // 2. Apply kits to any InstanceGroups owning members that came in via
  //    `globalStaticObjects` at scene load. Tile-streamed groups are handled
  //    by `TiledObjectStreamer` on group creation.
  //
  // `farInclude` lifts the kit's Skip-distance cull on the matching group so
  // landmark objects (castles, towers) keep rendering past Skip distance with
  // whatever LOD their kit's tail level provides. Effect is per-group; any
  // instance routed to the same `(mesh, material)` group inherits the lift.
  //
  // Pre-pass: collect the set of models whose globalStaticObjects has at least
  // one flagged placement. If we instead read `farInclude` only on the def
  // we're currently processing, the first def of a multi-def model would
  // install the kit (with Skip if unflagged) and later flagged defs would be
  // unable to undo it because the group's `lodLevelCount > 0`.
  const suppressSkipModels = new Set<string>();
  for (const objDef of manifest.globalStaticObjects ?? []) {
    if (objDef.farInclude) suppressSkipModels.add(objDef.model);
  }

  const im = scene.instanceManager;
  const seenGroups = new Set<InstanceGroup>();
  for (const objDef of manifest.globalStaticObjects ?? []) {
    const objects = namedObjects.get(objDef.name);
    if (!objects) continue;
    const kit = scene.modelLodKits.get(objDef.model);
    if (!kit) continue;
    const suppressSkip = suppressSkipModels.has(objDef.model);
    for (const obj of objects) {
      const group = im.getGroupForObject(obj);
      if (!group || seenGroups.has(group)) continue;
      seenGroups.add(group);
      if (group.lodLevelCount === 0) {
        applyModelLodKitToGroup(scene, kit, group, suppressSkip);
      } else if (suppressSkip) {
        // Kit was already installed (e.g. by `TiledObjectStreamer` if this
        // model also has per-tile placements landing in the same shared IG).
        // Undo the Skip cull so flagged globals override.
        group.skipDistanceSq = null;
      }
    }
  }
}
